import { Button } from '../button/button';
import { WorkspaceInvitationsProps } from './types';
import { Mail, Send, Check, X } from 'lucide-react';

export function WorkspaceInvitations({
  incomingInvitations,
  sentInvitations,
  isAdmin,
  acceptInvitation,
  declineInvitation,
  cancelInvitation,
}: WorkspaceInvitationsProps) {
  const hasIncoming = !!incomingInvitations?.length;
  const hasSent = !!sentInvitations?.length && isAdmin;
  const hasAny = hasIncoming || hasSent;

  if (!hasAny) {
    return (
      <div className="rounded-lg border border-[#00000010] bg-prompt-safecomwhite p-4 text-center">
        <Mail className="mx-auto mb-2 h-8 w-8 text-prompt-safecomabbey" />
        <p className="text-sm text-prompt-safecomabbey">
          No pending invitations
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Received Invitations */}
      {hasIncoming && (
        <div className="rounded-lg border border-[#00000010] bg-prompt-safecomwhite p-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-prompt-safecomoutrageous-orange/10 rounded-full p-1.5">
                <Mail className="h-4 w-4 text-prompt-safecomoutrageous-orange" />
              </div>
              <h4 className="text-sm font-semibold text-prompt-safecomvulcan">
                Received
              </h4>
              <span className="bg-prompt-safecomoutrageous-orange/10 rounded-full px-2 py-0.5 text-xs font-medium text-prompt-safecomoutrageous-orange">
                {incomingInvitations.length}
              </span>
            </div>
          </div>
          <div className="space-y-2">
            {incomingInvitations.map((invite) => (
              <div
                key={invite?.id}
                className="flex items-center justify-between rounded-lg border border-[#00000008] bg-prompt-safecomfantasy p-3 transition-shadow hover:shadow-sm"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-prompt-safecomvulcan">
                    {invite?.invited_by}
                  </p>
                  <p className="mt-0.5 text-xs text-prompt-safecomabbey">
                    Invited you to join
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-green-200 text-green-700 hover:bg-green-50 hover:text-green-800"
                    onClick={() =>
                      acceptInvitation({
                        invitationId: invite?.id,
                        invitedByEmail: invite?.invited_by,
                        workspaceId: invite?.workspace_id,
                      })
                    }
                  >
                    <Check className="mr-1 h-3 w-3" />
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                    onClick={() =>
                      declineInvitation({
                        id: invite.id,
                        email: invite.invited_email,
                        invitedByEmail: invite.invited_by,
                        workspaceId: invite.workspace_id,
                      })
                    }
                  >
                    <X className="mr-1 h-3 w-3" />
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sent Invitations (Admin only) */}
      {hasSent && (
        <div className="rounded-lg border border-[#00000010] bg-prompt-safecomwhite p-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-prompt-safecomoutrageous-orange/20 rounded-full p-1.5">
                <Send className="h-4 w-4 text-prompt-safecomoutrageous-orange" />
              </div>
              <h4 className="text-sm font-semibold text-prompt-safecomvulcan">
                Sent
              </h4>
              <span className="bg-prompt-safecomabbey/10 rounded-full px-2 py-0.5 text-xs font-medium text-prompt-safecomabbey">
                {sentInvitations.length}
              </span>
            </div>
          </div>
          <div className="space-y-2">
            {sentInvitations.map((invite) => (
              <div
                key={invite?.id}
                className="flex items-center justify-between rounded-lg border border-[#00000008] bg-prompt-safecomfantasy p-3 transition-shadow hover:shadow-sm"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-prompt-safecomvulcan">
                    {invite?.invited_email}
                  </p>
                  <p className="mt-0.5 text-xs text-prompt-safecomabbey">
                    Pending acceptance
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-prompt-safecomabbey hover:border-red-200 hover:text-red-700"
                  onClick={() =>
                    cancelInvitation({
                      id: invite.id,
                      email: invite.invited_email,
                    })
                  }
                >
                  <X className="mr-1 h-3 w-3" />
                  Cancel
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
